import { ref } from "vue";
import { db } from "@src/services/db";
import { useAuthStore } from "@src/stores/auth";
import { useChatSessionStore } from "@src/stores/chatSession";
import { reactToMessage } from "@src/services/messageReactions";
import type { ChatMessageReaction } from "@src/types/ChatMessageReaction";

export function useMessageReactions() {
    const auth = useAuthStore();
    const sessionStore = useChatSessionStore();

    const pendingByMessage = ref<Record<number, boolean>>({});

    function getMsg(messageId: number) {
        return sessionStore.selectedSession?.messages?.find(m => Number(m.id) === Number(messageId));
    }

    async function toggleReactionLocal(messageId: number, emoji: string) {
        const uid = auth.user?.id;
        if (!uid) return;

        const msg = getMsg(messageId);
        if (!msg) return;

        if (pendingByMessage.value[messageId]) return;
        pendingByMessage.value[messageId] = true;

        const prev: ChatMessageReaction[] = (((msg as any).reactions ?? []) as ChatMessageReaction[]).slice();

        try {
            const reactions: ChatMessageReaction[] = (((msg as any).reactions ?? []) as ChatMessageReaction[]);
            const mineIdx = reactions.findIndex(r => r.user_id === uid && r.emoji === emoji);

            let next: ChatMessageReaction[];
            let apiReaction: string;

            if (mineIdx !== -1) {
                next = reactions.filter((_, i) => i !== mineIdx);
                apiReaction = "remove";
            } else {
                const withoutMine = reactions.filter(r => r.user_id !== uid);
                next = [...withoutMine, { emoji, user_id: uid }];
                apiReaction = emoji;
            }

            (msg as any).reactions = next;

            try {
                await db.messages.update(messageId as any, { reactions: next, updated_at: Date.now() } as any);
            } catch {}

            await reactToMessage({ messageId, reaction: apiReaction });
        } catch {
            (msg as any).reactions = prev;
            try {
                await db.messages.update(messageId as any, { reactions: prev, updated_at: Date.now() } as any);
            } catch {}
        } finally {
            pendingByMessage.value[messageId] = false;
        }
    }

    function isPending(messageId: number) {
        return !!pendingByMessage.value[messageId];
    }

    return { toggleReactionLocal, isPending };
}
